<template>
  <div class="documents-secure-list-container">
    <router-view
      name="sidebar"
      @save="refresh"
      @hidden="navigateToSecureList"
      @toggleEdit="navigateToEditForm"
      @selectTemplate="handleSelectTemplate"
    />
    <document-secure-toolbar v-if="parent != parentTypes.EDITOR && !includesDeleted" />
    <div class="aula-documentsSecureList-container page-layout-padding">
      <h1 v-if="!includesDeleted" class="document-title">
        <b-btn
          variant="link"
          class="file-document"
          @click="$router.push({ name: 'documents' })"
          @keypress.enter="$router.push({ name: 'documents' })"
        >
          {{ $getText('DOCUMENTS_TYPES_PAGE_TITLE') }}
        </b-btn>
        <span class="breadcumb-spacer">/</span>
        <strong>{{ $getText('DOCUMENTS_TYPES_SECURE_FILES_TITLE') }}</strong>
      </h1>
      <div v-else class="deleted-secure-documents-text-container">
        <h1 class="header-text">{{ $getText('DELETED_SECURE_DOCUMENTS_HEADER') }}</h1>
        <p class="description-text">{{ $t('document.deleted_files_description') }}</p>
      </div>
      <div :data-sticky="isEnableStickyHeader" class="header-actions-container">
        <b-row v-if="profile.role === portalRoles.EMPLOYEE" class="mb-3">
          <b-col sm="12" md="4" class="mb-1">
            <aula-search-recipients
              id="filter-group"
              :doc-types-api="[docTypes.GROUP]"
              :from-module="moduleTypes.SECURE_DOCUMENT"
              :show-search-icon="true"
              :multiple="true"
              :list-my-groups-on-focus="true"
              :split-groups-participants="false"
              :enable-relatives-for-students="false"
              :close-dropdown-after-select="false"
              :allow-create="false"
              :scope-employees-to-institution="false"
              :placeholder-textkey="'DOCUMENTS_SECURE_SEARCH_GROUP'"
              :clearable="!isShowDeletedDocuments"
              @emitSelected="updateFilter"
            />
          </b-col>
          <b-col sm="12" md="4" class="mb-1">
            <aula-select
              v-model="selectedFilterPerson"
              filterable
              class="mb-1"
              :aria-label="$t('label.search_by_child')"
              :show-search-icon="!isShowDeletedDocuments"
              :clearable="canShowSelectedPersonFilterClearButton"
              @change="updateFilter()"
              @emitClear="handleClearSelectedFilterPersonFilter"
            >
              <aula-option v-for="(filter, i) in filterPerson" :key="i" :label="filter.text" :value="filter.value" />
            </aula-select>
          </b-col>
          <b-col v-if="profile.role !== portalRoles.GUARDIAN" sm="12" md="4" class="mb-1">
            <aula-select
              v-model="selectedFilterCategory"
              filterable
              :aria-label="$t('label.search_by_category')"
              :show-search-icon="!isShowDeletedDocuments"
              :clearable="canShowSelectedCategoryFilterClearButton"
              @change="updateFilter()"
              @emitClear="handleClearSelectedCategoryFilter"
            >
              <aula-option v-for="(filter, i) in filterCategory" :key="i" :label="filter.text" :value="filter.value" />
            </aula-select>
          </b-col>
        </b-row>
        <div v-if="canShowMultipleFileActionButtons" class="filter multiple-file-actions">
          <b-btn variant="primary" @click="addSharingGroups()">
            {{ $t('label.share') }}
          </b-btn>
          <b-btn
            v-if="hasPermission(permissionEnum.EXPORT_SECURE_FILES)"
            variant="primary"
            @click="showModalExportPDF()"
          >
            {{ $t('label.export') }}
          </b-btn>
          <b-btn
            v-if="hasPermission(permissionEnum.JOURNALING_TO_ESDH)"
            variant="primary"
            @click="handleMultipleEsdhExportClicked()"
          >
            {{ multipleExportLabel }}
          </b-btn>
          <b-btn
            v-if="profile.role === portalRoles.EMPLOYEE"
            variant="primary"
            @click="handleMultipleRemoveAssociationClicked()"
          >
            {{ $t('document.remove_association') }}
          </b-btn>
        </div>
      </div>
      <div v-if="profile.role === portalRoles.GUARDIAN">
        {{ $getText('DOCUMENTS_SHARED_OVERVIEW_GUARDIAN_WARNING') }}
      </div>
      <div
        v-if="!includesDeleted"
        :data-padding-top="canShowMultipleFileActionButtons"
        :data-sticky="isEnableStickyHeader"
        class="secure-files-filter-container"
      >
        <div v-if="profile.role === portalRoles.EMPLOYEE" class="selected-document-count">
          <i18n path="label.number_of_selected">
            <template #number>
              <strong>{{ status.length }}</strong>
            </template>
            <template #total>
              <strong>{{ totalDocumentCount }}</strong>
            </template>
          </i18n>
        </div>
        <div class="dropdown-filter-container">
          <b-dropdown class="dropdown-select filter-dropdown" right>
            <template #button-content>
              {{ getFilterLabel(handlingFilter) }}
              <Icon class="filter-arrow" :name="iconClassEnum.DOWN_ARROW" />
            </template>
            <template #default>
              <b-dropdown-item
                v-for="(option, key) in filterOptions"
                :key="key"
                @click="handleFilterChanged(option.value)"
              >
                <Icon
                  v-if="getCanShowActiveFilterIcon(option.value)"
                  class="active-filter-icon"
                  :name="iconClassEnum.PRESENCE_CHECK"
                />
                {{ option.label }}
              </b-dropdown-item>
            </template>
          </b-dropdown>
          <b-dropdown ref="sorter" class="dropdown-select sorter" right>
            <template slot="button-content">
              <span class="sort" :class="sortDirection" />
              {{ $getText(sortTexKeyMap.get(sort)) }}
              <i class="icon icon-Aula_down-arrow" />
            </template>
            <b-dropdown-item
              :class="sort === sortTypes.TITLE ? sortDirection : ''"
              @click="setSortOrder(sortTypes.TITLE)"
            >
              {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_TITLE') }}
            </b-dropdown-item>
            <b-dropdown-item
              :class="sort === sortTypes.UPDATED_AT ? sortDirection : ''"
              @click="setSortOrder(sortTypes.UPDATED_AT)"
            >
              {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_EDITED') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <ObservingContainer :options="observerOptions">
        <div class="table">
          <div
            :data-parent-type="parent"
            :data-padding-top="canShowMultipleFileActionButtons"
            :data-sticky="isEnableStickyHeader"
            class="table-row header"
          >
            <div v-if="canShowMultiSelectAction" class="table-cell multi-select-header check">
              <b-form-checkbox
                v-model="allSelected"
                :aria-label="selectedAlAriaLabel"
                @change="onToggleAllClicked"
              ></b-form-checkbox>
            </div>
            <div class="table-cell title-header">
              {{ $t('label.title') }}
            </div>
            <div v-if="!isShowDeletedDocuments" class="table-cell category-header">
              {{ $t('label.category') }}
            </div>
            <div class="table-cell description-header">
              {{ $t('label.description') }}
            </div>
            <div class="table-cell regarding-children-header">
              {{ $t('label.child') }}
            </div>
            <div class="table-cell">{{ documentsTableTimestampHeader }}</div>
            <div v-if="profile.role === portalRoles.EMPLOYEE" class="table-cell" />
          </div>
          <div
            v-if="canShowMultiSelectAction"
            :data-padding-top="canShowMultipleFileActionButtons"
            class="table-row mobile-header"
          >
            <div class="table-cell"></div>
            <div class="table-cell">
              <b-form-checkbox
                v-model="allSelected"
                :aria-label="selectedAlAriaLabel"
                @change="onToggleAllClicked"
              ></b-form-checkbox>
            </div>
          </div>
          <div
            v-for="(item, i) in documents"
            :key="'d' + i"
            class="table-row body"
            tabindex="0"
            @click="rowClickAction(item)"
          >
            <div v-if="canShowMultiSelectAction" class="table-cell not-collapsed check">
              <span class="sr-only">{{ $getText('ARIA_LABEL_OPEN_EDIT_DOCUMENT') }}</span>
              <b-form-checkbox v-model="status" :value="item.id" @click.native.stop @change="onToggleClicked">
                <span class="sr-only">{{ $getText('ARIA_LABEL_CHOOSE_DOCUMENT') }}</span>
              </b-form-checkbox>
            </div>
            <div class="table-cell title not-collapsed">
              <div v-if="item.documentType == documentTypes.EXTERNAL" class="filetype">
                {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_IMPORTED_FILE') }}
              </div>
              <badge-notification
                v-if="hasNewNotification(item.id) && !includesDeleted"
                :show-exclamation-icon-instead-of-amount="true"
                class="documents badge-notification"
              />
              <strong>{{ item.title }}</strong>
              <div v-if="item.isSharedWithGuardian && profile.role != portalRoles.GUARDIAN" class="marking-text">
                {{ $getText('DOCUMENTS_SHARED_WITH_GUARDIAN_MARK') }}
              </div>
            </div>
            <div v-if="!isShowDeletedDocuments" class="table-cell not-collapsed">
              {{ item.category }}
            </div>
            <div class="table-cell not-collapsed">
              {{ getDocumentDescription(item) }}
            </div>
            <div v-if="parent != parentTypes.EDITOR" class="table-cell not-collapsed">
              <span v-for="(shared, index) in item.associatedInstitutionProfiles" :key="'sp' + index">
                <span v-if="index > 0">, </span>{{ $getProfileDisplayName(shared) }}
              </span>
            </div>
            <div class="table-cell not-collapsed">
              <nobr> {{ getDocumentsTableDate(item) }} </nobr>
              <nobr> {{ getDocumentsTableTime(item) }}</nobr>
            </div>
            <div v-if="!isDocumentFormShown" class="table-cell collapsed-only">
              <div class="pull-left">
                <div class="item">
                  <badge-notification
                    v-if="hasNewNotification(item.id)"
                    :show-exclamation-icon-instead-of-amount="true"
                    class="documents badge-notification"
                  />
                  <strong>{{ item.title }}</strong>
                  ({{ item.category }})
                  <div v-if="item.isSharedWithGuardian && profile.role != portalRoles.GUARDIAN" class="marking-text">
                    {{ $getText('DOCUMENTS_SHARED_WITH_GUARDIAN_MARK') }}
                  </div>
                  <div v-if="item.documentType == documentTypes.EXTERNAL" class="filetype">
                    {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_IMPORTED_FILE') }}
                  </div>
                </div>
                <div v-if="getDocumentDescription(item)" class="item">
                  <strong>{{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_DESCRIPTION') }}:</strong>
                  {{ getDocumentDescription(item) }}
                </div>
                <div class="item">
                  <strong>{{ actionTitle }}:</strong>
                  <nobr> {{ getDocumentsTableDate(item) }} </nobr>
                  <nobr> {{ getDocumentsTableTime(item) }}</nobr>
                </div>
                <div class="item">
                  <strong>{{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_ASSOCIATION') }}:</strong>
                  <span v-for="(association, index) in item.associatedInstitutionProfiles" :key="index">
                    <template v-if="index > 0">, </template>{{ $getProfileDisplayName(association) }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="parent != parentTypes.EDITOR && profile.role === portalRoles.EMPLOYEE"
              class="table-cell actions"
            >
              <template v-if="includesDeleted">
                <AulaButton
                  :title="$getText('RESTORE')"
                  :aria-label="$getText('RESTORE')"
                  class="restore-btn"
                  variant="link"
                  @click.stop="onRestoreClicked(item)"
                >
                  <Icon :name="iconClassEnum.RESTORE" class="restore-icon" />
                  <p class="restore-text">{{ $getText('RESTORE') }}</p>
                </AulaButton>
              </template>
              <template v-else>
                <b-form-checkbox
                  v-if="hasPermission(permissionEnum.HANDLE_SECURE_FILES)"
                  v-model="status"
                  class="collapsed-only choose-document"
                  :value="item.id"
                  @click.native.stop
                  @change="onToggleClicked"
                >
                  <span class="sr-only">{{ $getText('ARIA_LABEL_CHOOSE_DOCUMENT') }}</span>
                </b-form-checkbox>
                <b-dropdown :id="'documents-functionalities' + item.id" class="dropdown-select" right @click.prevent>
                  <template slot="button-content">
                    <span class="sr-only"> {{ $t('document.functionalities') }}</span>
                    <Icon :name="iconClassEnum.ELIPSES" />
                  </template>
                  <b-dropdown-item v-if="item.isLocked" class="is-disabled" @click.stop>
                    {{ $getText('DOCUMENTS_SHARED_DOCUMENT_IS_LOCKED') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="item.canEditLockedStatus && !item.isLocked"
                    @click.stop="toggleLockDocument(item)"
                  >
                    {{ $getText('DOCUMENTS_SHARED_LOCK_DOCUMENT') }}
                  </b-dropdown-item>
                  <div
                    :id="`unlock-${item.id}}`"
                    :class="getCanEditLockStatus(item) ? 'disabled-drop-down' : ''"
                    @click.stop
                  >
                    <b-dropdown-item
                      v-if="getCanUnlock(item)"
                      :disabled="getCanEditLockStatus(item)"
                      @click.stop="toggleLockDocument(item, false)"
                    >
                      {{ $getText('DOCUMENTS_SHARED_UNLOCK_DOCUMENT') }}
                    </b-dropdown-item>
                  </div>
                  <b-tooltip
                    v-if="getCanEditLockStatus(item)"
                    :target="`unlock-${item.id}}`"
                    triggers="hover"
                    placement="top"
                  >
                    <div class="document-export-status">
                      {{ getDisabledUnlockOptionTooltip(item) }}
                    </div>
                  </b-tooltip>
                  <template v-if="item.canEdit">
                    <b-dropdown-item @click.stop="showModalSharedWith(item.id, item.documentType)">
                      {{ $getText('DOCUMENTS_IMPORT_FILE_LABEL_DOCUMENT_SHARED_WITH') }}
                    </b-dropdown-item>
                  </template>
                  <b-dropdown-item @click.stop="seeHistory(item)">
                    {{ $getText('DOCUMENTS_SHARED_OVERVIEW_MENU_REVISION') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click.stop="downloadDocument(item)">
                    {{
                      item.documentType == documentTypes.RICHDOCUMENT ||
                      item.documentType == documentTypes.INTERNAL ||
                      item.documentType == documentTypes.NOTE
                        ? $getText('DOCUMENTS_SHARED_OVERVIEW_MENU_DOWNLOAD_INTERNAL')
                        : $getText('DOCUMENTS_SHARED_OVERVIEW_MENU_DOWNLOAD')
                    }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      item.documentType !== documentTypes.EXTERNAL &&
                      hasPermissionOnInstitution(permissionEnum.EXPORT_SECURE_FILES, item.institutionCode)
                    "
                    @click.stop="showModalExportPDF(item)"
                  >
                    {{ $getText('DOCUMENTS_SHARED_OVERVIEW_MENU_EXPORT_PDF') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click.stop="copyLink(item.id, item.documentType)">
                    {{ $getText('DOCUMENTS_SHARED_OVERVIEW_MENU_COPY') }}
                  </b-dropdown-item>
                  <div
                    :id="`esdh-export-option-${item.id}`"
                    :class="getIsDisabledExportOption(item) ? 'disabled-drop-down' : ''"
                    @click.stop
                  >
                    <b-dropdown-item
                      v-if="getCanExportToESDH(item)"
                      :disabled="getIsDisabledExportOption(item)"
                      @click.stop="handleEsdhExportClicked(item)"
                    >
                      {{ getSingleExportEsdhLabel(item) }}
                    </b-dropdown-item>
                  </div>
                  <b-tooltip
                    v-if="getCanExportToESDH(item) && getIsDisabledExportOption(item)"
                    :target="`esdh-export-option-${item.id}`"
                    triggers="hover"
                    placement="top"
                  >
                    <div class="document-export-status">
                      {{ getEsdhExportOptionTooltipLabel(item) }}
                    </div>
                  </b-tooltip>
                  <div :class="getCanRemoveOwnAssociation(item) ? 'disabled-drop-down' : ''" @click.stop>
                    <b-dropdown-item
                      v-if="getCanRemoveOwnAssociation(item)"
                      @click.stop="handleRemoveAssociationClicked(item)"
                    >
                      {{ $t('document.remove_association') }}
                    </b-dropdown-item>
                  </div>
                  <div
                    :id="`delete-option-${item.id}`"
                    :class="getCanDeleteDocument(item) ? 'disabled-drop-down' : ''"
                    @click.stop
                  >
                    <b-dropdown-item
                      v-if="getCanShowDeleteOption(item)"
                      :disabled="getCanDeleteDocument(item)"
                      @click.stop="openDeleteDocumentConfirmModal(item)"
                    >
                      {{ $getText('DOCUMENTS_SHARED_DELETE_MENU') }}
                    </b-dropdown-item>
                  </div>
                  <b-tooltip
                    v-if="getCanShowDeleteOption(item) && getCanDeleteDocument(item)"
                    :target="`delete-option-${item.id}`"
                    triggers="hover"
                    placement="top"
                  >
                    {{ $t('document.file_is_being_journalized') }}
                  </b-tooltip>
                </b-dropdown>
                <IconContainer
                  v-if="getJournalingStatusIcon(item)"
                  :id="`esdh-export-status-${item.id}`"
                  class="document-icon-container"
                  :data-warning="item.journalingStatus === esdhJournalingStatus.FAILED"
                  @click.stop
                >
                  <Icon :name="getJournalingStatusIcon(item)" />
                </IconContainer>
                <b-tooltip
                  v-if="getJournalingStatusIcon(item)"
                  :target="`esdh-export-status-${item.id}`"
                  triggers="hover"
                  placement="top"
                >
                  <div class="document-export-status">
                    {{ getJournalingStatus(item) }}
                  </div>
                </b-tooltip>
                <IconContainer v-if="item.isLocked" class="document-icon-container">
                  <Icon :name="iconClassEnum.LOCK" />
                </IconContainer>
                <AulaButton
                  v-else-if="item.canEdit"
                  variant="link"
                  class="document-icon-container"
                  :aria-label="$getText('ARIA_LABEL_EDIT')"
                  @click.stop="editElement(item.id, item.documentType)"
                  @keydown.enter.stop="editElement(item.id, item.documentType)"
                >
                  <IconContainer class="document-icon-container">
                    <Icon :name="iconClassEnum.EDIT_PEN" />
                  </IconContainer>
                </AulaButton>
                <IconContainer v-if="item.hasMedia" class="document-icon-container">
                  <Icon :name="iconClassEnum.ATTACH_DOC" />
                </IconContainer>
              </template>
            </div>
          </div>
        </div>
        <aula-spinner v-if="isLoading" />
        <ObservedTarget v-if="documents.length > 0" @onIntersecting="onBottomReached" />
      </ObservingContainer>
      <div v-if="documentItems.documents && documentItems.documents.length == 0" class="no-documents">
        {{
          $getText(
            profile.role === portalRoles.GUARDIAN
              ? 'DOCUMENTS_SECURE_NO_DOCUMENTS_GUARDIAN'
              : 'DOCUMENTS_SECURE_NO_DOCUMENTS'
          )
        }}
      </div>
    </div>
    <aula-modal
      ref="documentModal"
      :hide-footer="true"
      :is-scroll-top="false"
      :header-text-from-textkey="false"
      :header-text="liveDocument.title"
      @hidden="$router.push({ name: 'documentsSecureList' })"
    >
      <template v-if="liveDocument.creator != null">
        {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_CREATED') }}: {{ liveDocument.creator.name }}<br />
      </template>
      <template v-if="liveDocument.associatedGroups != null && liveDocument.associatedGroups.length > 0">
        {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_GROUP') }}:
        {{ liveDocument.associatedGroups.map(group => group.name).join(', ') }}<br />
      </template>
      <template
        v-if="
          liveDocument.associatedInstitutionProfiles != null && liveDocument.associatedInstitutionProfiles.length > 0
        "
      >
        {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_ASSOCIATION') }}:
        {{ liveDocument.associatedInstitutionProfiles.map(group => group.name).join(', ') }}<br />
      </template>
      {{ $getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_CATEGORY') }}: {{ liveDocument.category }}<br /><br />
      <div v-if="liveDocument.content != null" class="mb-3" v-html="liveDocument.content.html" />
      <attachment-thumbnails :attachments="liveDocument.attachments" :parent="parentTypes.DOCUMENTS" />
      <attachment-list :attachments="liveDocument.attachments" :show-media="true" />
    </aula-modal>
    <aula-modal
      ref="warningDownloadSensitiveFile"
      ok-text="YES"
      @cancelClicked="$refs.warningDownloadSensitiveFile.hide()"
      @okClicked="downloadSensitiveFile()"
    >
      {{ $getText('DOCUMENTS_ALERT_DOWNLOAD_SENSITIVE_FILE_1') }}<br />
      {{ $getText('DOCUMENTS_ALERT_DOWNLOAD_SENSITIVE_FILE_2') }}
    </aula-modal>
    <aula-modal
      ref="warningLockDocument"
      ok-text="YES"
      @okClicked="doLockDocument()"
      @cancelClicked="resetLockDocument()"
    >
      {{ lockDocumentWarning }} <br />
      {{ $getText('DOCUMENTS_ALERT_LOCK_DOCUMENT_2') }}
    </aula-modal>
    <aula-modal
      ref="warningUnlockBeingExportedDocument"
      :show-cancel="false"
      @okClicked="$refs.warningUnlockBeingExportedDocument.hide()"
    >
      {{ $t('document.unlock_being_exported_document_warning') }}
    </aula-modal>
    <aula-modal
      ref="warningShareWithDifferentInstitution"
      :show-cancel="false"
      :is-scroll-top="false"
      ok-text="YES"
      @okClicked="$refs.warningShareWithDifferentInstitution.hide()"
    >
      {{ $getText('DOCUMENTS_ALERT_SHARE_WITH_DIFFERENT_INSTITUTION') }}
    </aula-modal>
    <toastr ref="copyLinkToastr" :is-scroll-top="false" variant="success" :show-icon="true" icon="icon-Aula_check">
      {{ $getText('DOCUMENTS_TOASTR_COPY_LINK') }}
    </toastr>
    <aula-modal
      ref="modalSharedWith"
      :is-scroll-top="false"
      css-class="shared-with-modal"
      header-text="DOCUMENTS_SHARED_OVERVIEW_MENU_SHARE"
      ok-text="DOCUMENTS_SHARE_FOLDER_MODAL_SAVE"
      @cancelClicked="$refs.modalSharedWith.hide()"
      @okClicked="sharedUsersAndGroupsStore(false)"
    >
      <div class="container-scroll">
        <b-form-group :label="$getText('DOCUMENTS_SHARE_FOLDER_MODAL_USERPICKER')" label-for="input1">
          <aula-search-recipients
            ref="modalSharedWithrecipients"
            :select-all="false"
            url="findProfilesAndGroupsToShareDocument"
            :show-search-icon="true"
            :show-checkbox="false"
            :enable-relatives-for-students="false"
            :scope-employees-to-institution="false"
            :from-module="moduleTypes.SECURE_DOCUMENT"
            :reset-input="resetSharedWithInput"
            :invite-members-when-selecting-group-portal-role="true"
            :disable-portal-roles-members-when-expanding-group="[portalRoles.CHILD, portalRoles.GUARDIAN]"
            :hide-portal-roles-when-expanding-group="[portalRoles.CHILD, portalRoles.GUARDIAN]"
            :portal-roles="getSearchScope()"
            :include-self="true"
            :existing-participants="appendSharedUsersAndGroupsSet"
            :extra-a-p-i-params="{
              regardingChildren: workingDocument.regardingInstitutionProfileIds,
            }"
            @emitSelected="appendSharedUsersAndGroups"
          />
        </b-form-group>
        <b-container>
          <b-row v-if="currentShared.length > 0" class="pb-1">
            <b-col cols="7" />
            <b-col cols="5" class="text-center text-nowrap">
              {{ $getText('DOCUMENTS_SHARE_FOLDER_MODAL_CAN_EDIT') }}
            </b-col>
          </b-row>
          <b-row v-for="(sharedItem, index) in currentShared" :key="index">
            <b-col class="text">
              {{ sharedItem.displayName || sharedItem.groupName }}
            </b-col>
            <b-col cols="3" class="text-center align-self-center">
              <b-form-checkbox
                v-model="currentShared[index].canEdit"
                :disabled="sharedItem.role === portalRoles.GUARDIAN"
                :checked="sharedItem.canEdit"
              >
                <span class="sr-only">{{ $getText('ARIA_LABEL_CHOOSE_DOCUMENT') }}</span>
              </b-form-checkbox>
            </b-col>
            <b-col cols="1" class="align-self-center">
              <i
                class="icon-Aula_bin"
                tabindex="0"
                role="button"
                :aria-label="$getText('ARIA_LABEL_DELETE')"
                @click="currentShared.splice(index, 1)"
                @keydown.enter="currentShared.splice(index, 1)"
              />
            </b-col>
          </b-row>
        </b-container>
      </div>
    </aula-modal>
    <aula-modal
      ref="modalSharedWithGroups"
      :is-scroll-top="false"
      :is-loading="isUpdateSharing"
      css-class="document-sharing-modal shared-with-modal"
      header-text="DOCUMENTS_SHARED_OVERVIEW_MENU_SHARE"
      ok-text="DOCUMENTS_SHARE_FOLDER_MODAL_SAVE"
      @cancelClicked="$refs.modalSharedWithGroups.hide()"
      @okClicked="sharedUsersAndGroupsStore(true)"
    >
      <div class="container-scroll">
        <b-form-group :label="$getText('DOCUMENTS_SHARE_FOLDER_MODAL_USERPICKER')" label-for="input1">
          <aula-search-recipients
            :select-all="false"
            url="findProfilesAndGroupsToShareDocument"
            :enable-relatives-for-students="false"
            :scope-employees-to-institution="false"
            :show-search-icon="true"
            :reset-input="resetSharedWithInput"
            :from-module="moduleTypes.SECURE_DOCUMENT"
            :invite-members-when-selecting-group-portal-role="true"
            :disable-portal-roles-members-when-expanding-group="[portalRoles.CHILD, portalRoles.GUARDIAN]"
            :hide-portal-roles-when-expanding-group="[portalRoles.CHILD, portalRoles.GUARDIAN]"
            :portal-roles="getSearchScope()"
            :include-self="true"
            :existing-participants="appendSharedUsersAndGroupsSet"
            :extra-a-p-i-params="{ regardingChildren: intersectedChildrenIds }"
            @emitSelected="appendSharedUsersAndGroups"
          />
        </b-form-group>
        <b-container>
          <b-row v-if="currentShared.length > 0" class="pb-1">
            <b-col cols="7" />
            <b-col cols="5" class="text-center text-nowrap">
              {{ $getText('DOCUMENTS_SHARE_FOLDER_MODAL_CAN_EDIT') }}
            </b-col>
          </b-row>
          <b-row v-for="(sharedItem, index) in currentShared" :key="index" class="pb-1">
            <b-col class="text">
              {{ sharedItem.displayName || sharedItem.groupName }}
            </b-col>
            <b-col cols="3" class="text-center align-self-center">
              <b-form-checkbox
                v-model="currentShared[index].canEdit"
                :disabled="sharedItem.role === portalRoles.GUARDIAN"
                :checked="sharedItem.canEdit"
              >
                <span class="sr-only">{{ $getText('ARIA_LABEL_CHOOSE_DOCUMENT') }}</span>
              </b-form-checkbox>
            </b-col>
            <b-col cols="1" class="delete">
              <i
                class="icon-Aula_bin"
                tabindex="0"
                role="button"
                :aria-label="$getText('ARIA_LABEL_DELETE')"
                @click="currentShared.splice(index, 1)"
                @keydown.enter="currentShared.splice(index, 1)"
              />
            </b-col>
          </b-row>
        </b-container>
      </div>
    </aula-modal>
    <aula-modal
      ref="modalExportPDF"
      ok-text="DOWNLOAD"
      header-text="DOCUMENTS_SHARED_OVERVIEW_MENU_EXPORT"
      :disable-submit="isDisabledExport"
      :is-scroll-top="false"
      :is-loading="isDisabledExport && !isExportFailed"
      @cancelClicked="$refs.modalExportPDF.hide()"
      @okClicked="openFileUrl()"
    >
      {{ $getText('DOCUMENTS_ALERT_EXPORT_PDF_1') }} <br /><br />
      {{ $getText('DOCUMENTS_ALERT_EXPORT_PDF_2') }}
      <div class="row mb-1 mt-5 pt-5">
        <div class="col-sm-12 pt-1">
          <b-progress :value="fileProgress" />
        </div>
      </div>
      <b-alert variant="danger" :show="isExportFailed">
        {{ $getText('API_ERROR_EXPORT_PDF') }}
      </b-alert>
    </aula-modal>
    <aula-modal
      ref="modalSelectedWarning"
      header-text="DOCUMENTS_SHARED_OVERVIEW_MENU_LOCKED_WARNING"
      :show-cancel="false"
      :is-scroll-top="false"
      @okClicked="$refs.modalSelectedWarning.hide()"
    >
      {{ $getText('DOCUMENTS_ALERT_LOCKED_DOCUMENT_SELECTED') }}
      <br /><br />
      <ul>
        <li v-for="(item, index) in selectedLockedDocuments" :key="index">
          {{ item.title }}
        </li>
      </ul>
    </aula-modal>
    <aula-modal
      ref="modalSelectedWithoutExportWarning"
      header-text="DOCUMENTS_SHARED_OVERVIEW_MENU_LOCKED_WARNING"
      :show-cancel="false"
      :is-scroll-top="false"
      @okClicked="$refs.modalSelectedWithoutExportWarning.hide()"
    >
      {{ $getText('DOCUMENTS_ALERT_NO_EXPORT_RIGHT_DOCUMENT_SELECTED') }}
      <br /><br />
      <ul>
        <li v-for="(item, index) in selectedDocWithoutExportRight" :key="index">
          {{ item.title }}
        </li>
      </ul>
    </aula-modal>
    <aula-modal
      ref="exceededDocumentsExportToPdfWarningModal"
      :show-cancel="false"
      @okClicked="$refs.exceededDocumentsExportToPdfWarningModal.hide()"
    >
      <i18n path="document.exceeded_pdf_export_warning" tag="p">
        <template #total>
          <strong>{{ maxDocumentsPerExport }}</strong>
        </template>
        <template #remove>
          <strong>{{ exceedDocumentsToExport }}</strong>
        </template>
      </i18n>
    </aula-modal>
    <aula-modal
      ref="shareWithOtherMunicipalitiesError"
      :show-cancel="false"
      @okClicked="$refs.shareWithOtherMunicipalitiesError.hide()"
    >
      {{ $getText('API_ERROR_SHARE_WITH_OTHER_MUNICIPALITIES') }}
    </aula-modal>
    <aula-modal
      ref="deleteSecureDocumentConfirmModal"
      :ok-text="'BUTTON_YES'"
      @okClicked="deleteDocument"
      @cancelClicked="$refs.deleteSecureDocumentConfirmModal.hide()"
    >
      {{ $getText('DELETE_DOCUMENT_CONFIRM') }} '{{ selectedDocument.title }}'. <br />
      {{ $getText('DELETE_DOCUMENT_CONFIRM_2') }} <br />
      {{ $getText('DELETE_DOCUMENT_CONFIRM_3') }}
    </aula-modal>
    <AulaModal
      ref="restoreWarningModal"
      :ok-text="'BUTTON_YES'"
      @okClicked="confirmRestoringDeletedSecureDocument"
      @cancelClicked="cancelRestoring"
    >
      <p v-if="selectedDocument">
        {{ $getText('DELETED_SECURE_DOCUMENT_RESTORE_WARNING_1') }} <b>{{ selectedDocument.title }}</b
        >.<br />
        {{ $getText('DELETED_SECURE_DOCUMENT_RESTORE_WARNING_2') }}
      </p>
    </AulaModal>
    <AulaModal
      ref="esdhExportInvalidDocumentsWarningModal"
      :ok-text="hasValidEsdhDocuments ? 'BUTTON_YES' : 'OK'"
      :show-cancel="hasValidEsdhDocuments"
      :css-class="'document-warning-modal'"
      @okClicked="confirmRemoveInvalidDocuments"
      @cancelClicked="cancelRemoveInvalidDocuments"
    >
      <template v-if="hasDocumentsWithExceededChildren">
        <i18n path="document.exceeded_regarding_children_warning_1">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
        </i18n>
        <ul>
          <li v-for="(item, index) in exceedRegardingChildrenDocuments" :key="`${item.id}-${index}`">
            <IconContainer>
              <Icon :name="iconClassEnum.PAPER" />
            </IconContainer>
            <div class="document-title">
              {{ item.title }}
            </div>
          </li>
        </ul>
        <i18n v-if="hasValidEsdhDocuments" path="document.exceeded_regarding_children_warning_2">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
        </i18n>
      </template>
      <template v-else-if="hasDocumentsWithNoChildren">
        <i18n path="document.no_regarding_children_warning_1">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
        </i18n>
        <ul>
          <li v-for="(item, index) in noRegardingChildrenDocuments" :key="`${item.id}-${index}`">
            <IconContainer>
              <Icon :name="iconClassEnum.PAPER" />
            </IconContainer>
            <div class="document-title">
              {{ item.title }}
            </div>
          </li>
        </ul>
        <i18n v-if="hasValidEsdhDocuments" path="document.no_regarding_children_warning_2">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
        </i18n>
      </template>
      <template v-else-if="hasBeingJournalizedDocumentsToExport">
        <i18n path="document.export_being_journalized_again_warning_1">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
        </i18n>
        <ul>
          <li v-for="(item, index) in beingJournalizedDocuments" :key="`${item.id}-${index}`">
            <IconContainer>
              <Icon :name="iconClassEnum.PAPER" />
            </IconContainer>
            <div class="document-title">
              {{ item.title }}
            </div>
          </li>
        </ul>
        <i18n path="document.export_being_journalized_again_warning_2">
          <template #systemName>
            {{ recipientSystemName }}
          </template>
          <template v-if="hasValidEsdhDocuments" #extendedMessage>
            {{ $t('document.export_being_journalized_again_warning_3') }}.
          </template>
          <template v-else #extendedMessage>.</template>
        </i18n>
      </template>
      <span v-if="hasValidEsdhDocuments">
        <br />
        <br />
        {{ $t('warning.continue') }}
      </span>
    </AulaModal>
    <AulaModal
      ref="noExportRightWarningModal"
      :ok-text="hasValidEsdhDocuments ? 'BUTTON_YES' : 'OK'"
      :show-cancel="hasValidEsdhDocuments"
      :css-class="'document-warning-modal'"
      @okClicked="confirmRemoveDocumentsWithoutExportRight"
      @cancelClicked="cancelRemoveDocumentsWithoutExportRight"
    >
      <template v-if="hasDocumentsWithoutJournalizationRight">
        <i18n path="document.no_export_permission_warning_1">
          <template v-if="hasValidEsdhDocuments" #extendedMessage>
            , {{ $t('document.no_export_permission_warning_2') }}
          </template>
          <template v-else #extendedMessage>.</template>
        </i18n>
        <ul>
          <li v-for="(item, index) in documentsWithoutJournalizationRight" :key="`${item.id}-${index}`">
            <IconContainer>
              <Icon :name="iconClassEnum.PAPER" />
            </IconContainer>
            <div class="document-title">
              {{ item.title }}
            </div>
          </li>
        </ul>
      </template>
      <template v-else-if="hasDocumentsWithoutRecipientConfigured">
        {{ $t('document.no_recipients_configured_warning') }}
        <ul>
          <li v-for="(item, index) in documentsWithoutRecipientConfigured" :key="`${item.id}-${index}`">
            <IconContainer>
              <Icon :name="iconClassEnum.PAPER" />
            </IconContainer>
            <div class="document-title">
              {{ item.title }}
            </div>
          </li>
        </ul>
      </template>
      <span v-if="hasValidEsdhDocuments">
        {{ $t('document.continue_without_listed_files_confirmation') }}
      </span>
    </AulaModal>
    <AulaModal
      ref="removeAssociationWithoutEditRightWarningModal"
      :ok-text="'BUTTON_YES'"
      :css-class="'document-remove-association-warning-modal'"
      @okClicked="confirmRemoveDocumentAssociationWithoutEditRight"
      @cancelClicked="$refs.removeAssociationWithoutEditRightWarningModal.hide()"
    >
      <i18n path="document.remove_association_without_edit_right_warning" tag="p">
        <template #notText>
          <strong>{{ $t('label.not') }}</strong>
        </template>
      </i18n>
      <ul>
        <li v-for="(item, index) in documentsWithoutEditRights" :key="`${item.id}-${index}`">
          <IconContainer>
            <Icon :name="iconClassEnum.PAPER" />
          </IconContainer>
          <div class="document-title">
            {{ item.title }}
          </div>
        </li>
      </ul>
      <p>{{ $t('document.continue_without_listed_files_confirmation') }}</p>
    </AulaModal>
    <DocumentEsdhExportDrawer
      ref="esdhExportDrawer"
      :selected-documents="selectedEsdhDocumentsToExport"
      :recipient-system-name="recipientSystemName"
      @remove="onRemoveSelectedEsdhExportSecurefile"
      @sent="refresh"
    />
    <DocumentAssociationRemovalDrawer
      ref="associationRemovalDrawer"
      :selected-documents="selectedDocumentsToRemoveAssociation"
      @remove="onRemoveAssociationRemovalDocument"
      @sent="onSendingRemoveAssociation"
    />
  </div>
</template>

<script>
import { types } from '../../store/types/types';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DocumentSecureToolbar from './DocumentSecureToolbar.vue';
import AulaSearchRecipients from '../../../shared/components/AulaSearchRecipients';
import BadgeNotification from '../../../shared/components/BadgeNotification.vue';
import { permissionEnum } from '../../../shared/enums/permissionEnum.ts';
import { docTypes } from '../../../shared/enums/docTypes';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import { moduleTypes } from '../../../shared/enums/moduleTypes';
import { documentCategories } from '../../../shared/enums/documentCategories';
import { documentTypes } from '../../../shared/enums/documentTypes';
import { portalRoles } from '../../../shared/enums/portalRoles';
import AttachmentList from '../../../shared/components/AttachmentList';
import AttachmentThumbnails from '../../../shared/components/AttachmentThumbnails';
import { exportingStatus } from '../../../shared/enums/exportingStatus';
import { sortTypes } from '../../../shared/enums/sortTypes';
import { errorSubCodeTypes } from '../../../shared/enums/errorSubCodeTypes';
import requestCancelTokenMixin from '../../../shared/mixins/requestCancelTokenMixin';
import sortBy from 'lodash/sortBy';
import ObservingContainer from '../../../shared/libs/intersection-observer/components/Container.vue';
import ObservedTarget from '../../../shared/libs/intersection-observer/components/Target.vue';
import AulaButton from '../../../shared/components/AulaButton.vue';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import truncate from 'lodash/truncate';
import DocumentEsdhExportDrawer from './DocumentEsdhExportDrawer.vue';
import { esdhJournalingStatus } from '../../../shared/enums/esdhJournalingStatus';
import IconContainer from '../../../shared/components/IconContainer.vue';
import { esdhDataExportAdminService } from '../../../shared/services/api/esdhDataExportAdmin.service';
import DocumentAssociationRemovalDrawer from './DocumentAssociationRemovalDrawer.vue';
import chunk from 'lodash/chunk';
import button from '../../../shared/directives/button.js';
import { documentService } from '../../../shared/services/api/document.service';
import { DocumentFilter } from '../../business/document/documentFilter';
import { DocumentFilterOptionEnum } from '../../../shared/enums/documentFilterOption.enum';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { useUserProfileStore } from '../../../shared/stores';
import { notificationEventTypes } from '../../../shared/enums/notificationEventTypes';

const MAX_SECURE_NOTE_DESCRIPTION_LENGTH = 300;
const CHILD_LIMIT_FOR_ESDH_EXPORT = 28;
const UPDATE_SHARING_CHUNK_SIZE = 10;

export default {
  directives: {
    button,
  },
  components: {
    DocumentAssociationRemovalDrawer,
    IconContainer,
    DocumentEsdhExportDrawer,
    Icon,
    AulaButton,
    ObservedTarget,
    ObservingContainer,
    AttachmentList,
    AttachmentThumbnails,
    DocumentSecureToolbar,
    AulaSearchRecipients,
    BadgeNotification,
  },
  mixins: [requestCancelTokenMixin],
  props: {
    parent: { type: String, default: parentTypes.DOCUMENTS },
    includesDeleted: { type: Boolean, default: false },
  },
  data: function () {
    return {
      cleanDropDowns: false,
      isLoading: true,
      isUpdateSharing: false,
      permissionEnum: permissionEnum,
      portalRoles: portalRoles,
      categoriesList: documentCategories,
      docTypes: docTypes,
      parentTypes: parentTypes,
      moduleTypes: moduleTypes,
      documentTypes: documentTypes,
      sortTypes,
      paginationStart: 0,
      pageSize: 50,
      sort: this.parent === parentTypes.ADMINISTRATION ? sortTypes.DELETED_AT : sortTypes.UPDATED_AT,
      sortDirection: 'desc',
      sortTexKeyMap: new Map([
        [sortTypes.TITLE, 'DOCUMENTS_SHARED_OVERVIEW_TABLE_TITLE'],
        [sortTypes.UPDATED_AT, 'DOCUMENTS_SHARED_OVERVIEW_TABLE_UPDATEDAT'],
      ]),
      currentShared: [],
      editDrawer: false,
      appendSharedUsersAndGroupsSet: [],
      filterGroup: [],
      selectedFilterGroup: [],
      filterPerson: [],
      filterCategory: [],
      workingDocument: {
        content: { html: '' },
      },
      selectedFilterPerson: null,
      selectedFilterCategory: null,
      status: [],
      allSelected: false,
      errorMissingDoc: false,
      errorSetAssociation: false,
      errorGroupNotInInstitution: false,
      associatedChildren: [],
      currentChoosenGroupName: null,
      errorSetTitle: false,
      mediaToRemove: {},
      singleStatus: null,
      currentChoosenGroup: null,
      downloadFile: null,
      resetSharedWithInput: false,
      sharedWithGroup: {},
      lockDocumentWarning: null,
      changedDocument: null,
      isLocked: null,
      originalProfilePortalRole: null,
      fileProgress: 0,
      archiveDocumentTrackingTimer: null,
      isDisabledExport: true,
      selectedLockedDocuments: [],
      selectedDocWithoutExportRight: [],
      isExportFailed: false,
      intersectedChildrenIds: [],
      selectedDocument: {},
      documentsCancelTokenSource: null,
      selectedEsdhDocumentsToExport: [],
      institutionCodesCanExportToEsdh: [],
      esdhSystemNameByMunicipalityCode: new Map(),
      recipientSystemName: '',
      selectedDocumentsToRemoveAssociation: [],
      maxDocumentsPerExport: 100,
      handlingFilter: null,
    };
  },
  computed: {
    selectedAlAriaLabel() {
      return `${this.$t('document.select_all')} ${this.$t('label.number_of_selected', {
        number: this.status.length,
        total: this.totalDocumentCount,
      })}`;
    },
    esdhJournalingStatus() {
      return esdhJournalingStatus;
    },
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      selectedInstitution: types.GET_SELECTED_INSTITUTION,
      activeInstitutions: types.GET_ACTIVE_INSTITUTIONS,
      activeChildren: types.GET_ACTIVE_CHILDREN,
      documentItems: types.GET_DOCUMENTS,
      liveDocument: types.GET_DOCUMENTS_LIVE,
      hasPermission: types.HAS_PERMISSION,
      hasPermissionOnInstitution: types.HAS_PERMISSION_ON_INSTITUTION,
      notifications: types.GET_NOTIFICATIONS,
      institutions: types.GET_INSTITUTIONS,
      isMobile: types.GET_IS_MOBILE,
      isSteppedUp: types.GET_GLOBAL_STEPPED_UP,
      userGroups: types.GET_USER_GROUPS,
      groupMemberships: types.GET_GROUP_MEMBERSHIPS_LIGHT,
      archiveDocumentTrack: types.GET_ARCHIVE_DOCUMENT_TRACK,
      archiveDocumentProgress: types.GET_ARCHIVE_DOCUMENT_PROGRESS,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    isProfileLoaded() {
      return this.userProfileStore.isLoading === false;
    },
    filterOptions() {
      return DocumentFilter.getOptions(this.isEmployee, this.canFilterByJournalingStatus);
    },
    isEmployee() {
      return this.profile.role === this.portalRoles.EMPLOYEE;
    },
    isEnableStickyHeader() {
      return !this.includesDeleted && this.isEmployee;
    },
    canFilterByJournalingStatus() {
      return this.journalingInstitutionCodes.length > 0;
    },
    journalingInstitutionCodes() {
      return this.currentActiveInstitutions
        .map(institution => institution.institutionCode)
        .filter(institutionCode => this.hasPermissionOnInstitution(permissionEnum.JOURNALING_TO_ESDH, institutionCode));
    },
    canShowSelectedPersonFilterClearButton() {
      return !this.isShowDeletedDocuments && this.selectedFilterPerson;
    },
    canShowSelectedCategoryFilterClearButton() {
      return !this.isShowDeletedDocuments && this.selectedFilterCategory;
    },
    isShowDeletedDocuments() {
      return this.includesDeleted;
    },
    exceedDocumentsToExport() {
      return this.status.length - this.maxDocumentsPerExport;
    },
    totalDocumentCount() {
      return this.documentItems.totalCount ?? 0;
    },
    exceedRegardingChildrenDocuments() {
      return this.selectedEsdhDocumentsToExport.filter(doc => this.isDocumentExceedRegardingChildren(doc));
    },
    noRegardingChildrenDocuments() {
      return this.selectedEsdhDocumentsToExport.filter(doc => this.isDocumentHasNoRegardingChildren(doc));
    },
    beingJournalizedDocuments() {
      return this.selectedEsdhDocumentsToExport.filter(doc => this.isDocumentBeingJournalized(doc));
    },
    documentsWithoutJournalizationRight() {
      return this.selectedEsdhDocumentsToExport.filter(doc => this.isDocumentWithoutJournalizationRight(doc));
    },
    documentsWithoutRecipientConfigured() {
      return this.selectedEsdhDocumentsToExport.filter(doc => this.isDocumentWithoutRecipientConfigured(doc));
    },
    documentsWithoutEditRights() {
      return this.selectedDocumentsToRemoveAssociation.filter(doc => !doc.canEdit);
    },
    hasDocumentsWithExceededChildren() {
      return this.selectedEsdhDocumentsToExport.some(doc => this.isDocumentExceedRegardingChildren(doc));
    },
    hasDocumentsWithNoChildren() {
      return this.selectedEsdhDocumentsToExport.some(doc => this.isDocumentHasNoRegardingChildren(doc));
    },
    hasBeingJournalizedDocumentsToExport() {
      return this.selectedEsdhDocumentsToExport.some(doc => this.isDocumentBeingJournalized(doc));
    },
    hasDocumentsWithoutJournalizationRight() {
      return this.selectedEsdhDocumentsToExport.some(doc => this.isDocumentWithoutJournalizationRight(doc));
    },
    hasDocumentsWithoutRecipientConfigured() {
      return this.selectedEsdhDocumentsToExport.some(
        doc => !this.isDocumentWithoutJournalizationRight(doc) && this.isDocumentWithoutRecipientConfigured(doc)
      );
    },
    isValidToEsdhExport() {
      return (
        !this.hasDocumentsWithExceededChildren &&
        !this.hasDocumentsWithNoChildren &&
        !this.hasBeingJournalizedDocumentsToExport &&
        !this.hasDocumentsWithoutJournalizationRight &&
        !this.hasDocumentsWithoutRecipientConfigured
      );
    },
    hasInvalidDocumentsToRemoveAssociation() {
      return this.selectedDocumentsToRemoveAssociation.some(doc => !doc.canEdit);
    },
    actionTitle() {
      return this.includesDeleted
        ? this.$getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_DELETED')
        : this.$getText('DOCUMENTS_SHARED_OVERVIEW_TABLE_EDITED');
    },
    observerOptions() {
      return { rootMargin: '50%' };
    },
    documentsTableTimestampHeader() {
      return this.includesDeleted ? this.$t('label.deleted') : this.$t('label.edited');
    },
    documents() {
      return this.documentItems?.documents || [];
    },
    documentPayload() {
      const index = this.paginationStart * this.pageSize;
      const limit = this.pageSize;
      const sortings = [
        {
          order: this.sortDirection,
          field: this.sort,
        },
      ];
      let filterInstitutionProfileIds = [];
      let filterRegardingGroupIds = [];
      let filterRegardingStudentIds = [];
      const filterDocumentCategories = [];

      if (this.activeChildren?.length > 0) {
        filterRegardingStudentIds = filterRegardingStudentIds.concat(this.activeChildren);
      }
      if (this.selectedFilterGroup) {
        filterRegardingGroupIds = this.selectedFilterGroup;
      }
      if (this.selectedFilterPerson) {
        filterRegardingStudentIds = [];
        filterRegardingStudentIds.push(this.selectedFilterPerson);
      }
      if (this.selectedFilterCategory) {
        filterDocumentCategories.push(this.selectedFilterCategory);
      }
      const activeInstitutions = this.activeInstitutions ?? [this.selectedInstitution.institutionCode];
      if (activeInstitutions.length > 0) {
        const result = this.institutions.reduce((result, inst) => {
          if (activeInstitutions.indexOf(inst.institutionCode) != -1) {
            result.push(inst.institutionProfileId);
          }
          return result;
        }, []);
        filterInstitutionProfileIds = result;
      }

      let params;
      const documentInfo = {
        index: index,
        limit: limit,
        sortings: sortings,
        filterRegardingGroupIds: filterRegardingGroupIds,
        filterDocumentCategories: filterDocumentCategories,
        filterRegardingStudentIds: filterRegardingStudentIds,
        filterInstitutionProfileIds: filterInstitutionProfileIds,
      };

      if (this.includesDeleted) {
        params = this.getDeletedSecureDocumentParams(documentInfo);
      } else {
        params = this.getSecureDocumentParams(documentInfo);
      }

      return {
        params: params,
        cancelToken: this.documentsCancelTokenSource.token,
      };
    },
    isDocumentFormShown() {
      return [
        'documentsSecureImport',
        'documentsSecureEdit',
        'documentsSecureNew',
        'createDocumentNote',
        'viewDocument',
      ].includes(this.$route.name);
    },
    canShowMultipleFileActionButtons() {
      return this.status.length >= 1 && !this.includesDeleted;
    },
    canShowMultiSelectAction() {
      return this.hasPermission(permissionEnum.HANDLE_SECURE_FILES) && !this.includesDeleted;
    },
    isProfileSwitched() {
      return this.originalProfilePortalRole != this.profile.role;
    },
    currentActiveInstitutions() {
      const activeInstitutionCodes = this.activeInstitutions ?? [this.selectedInstitution.institutionCode];
      return this.institutions.filter(
        institution => activeInstitutionCodes.indexOf(institution.institutionCode) !== -1
      );
    },
    multipleExportsEsdhSystemName() {
      let systemName = this.$t('document.esdh_system');

      const isCurrentUserInOneMunicipality = this.currentActiveMunicipalityCodes.length === 1;
      const hasEsdhConfiguration = this.esdhSystemNameByMunicipalityCode.size > 0;
      if (isCurrentUserInOneMunicipality && hasEsdhConfiguration) {
        [systemName] = this.esdhSystemNameByMunicipalityCode.values();
      }

      return systemName;
    },
    multipleExportLabel() {
      const systemName = this.multipleExportsEsdhSystemName;
      return this.$t('document.journalise_to_system', { system: systemName });
    },
    currentActiveMunicipalityCodes() {
      return this.currentActiveInstitutions.map(institution => institution.municipalityCode);
    },
    selectedDocuments() {
      return this.documentItems.documents.filter(item => this.status.indexOf(item.id) !== -1);
    },
    hasValidEsdhDocuments() {
      return this.selectedEsdhDocumentsToExport.some(doc => this.isDocumentValidForEsdhExport(doc));
    },
  },
  watch: {
    activeInstitutions() {
      if (this.hasPermission(this.permissionEnum.ACCESS_SECURE_FILESHARING) && !this.isProfileSwitched) {
        this.refresh();
        this.resetFilters();
        this.getAccessibleInstitutionCodesForEsdhExport();
      } else {
        this.$router.push({ name: 'documents' });
      }
    },
    activeChildren() {
      this.refresh();
    },
    documentItems() {
      if (this.allSelected) {
        this.markAllDocumentsAsSelected();
      }
    },
  },
  mounted() {
    this.initDocuments();
  },
  created() {
    this.originalProfilePortalRole = this.profile.role;
  },
  methods: {
    ...mapActions({
      restoreDeletedSecureDocument: types.ACTION_RESTORE_DELETED_DOCUMENT_ADMIN,
      loadDeletedDocuments: types.ACTION_GET_DELETED_DOCUMENTS_ADMIN,
      appendDeletedDocuments: types.ACTION_GET_MORE_DELETED_DOCUMENTS_ADMIN,
      loadDocuments: types.ACTION_GET_DOCUMENTS,
      appendDocuments: types.ACTION_GET_MORE_DOCUMENTS,
      editSharings: types.ACTION_EDIT_SHARINGS,
      editSetupDocument: types.ACTION_LOAD_INTERNAL_DOCUMENT,
      editSetupExternal: types.ACTION_LOAD_EXTERNAL_DOCUMENT,
      deleteNotifications: types.DELETE_NOTIFICATIONS,
      lockDocument: types.LOCK_DOCUMENT,
      loadGroupMemberships: types.LOAD_GROUP_MEMBERSHIPS_LIGHT,
      createArchiveSecureDocuments: types.ACTION_CREATE_ARCHIVE_SECURE_DOCUMENTS,
      trackArchiveSecureDocuments: types.ACTION_TRACK_ARCHIVE_SECURE_DOCUMENTS,
      deleteSecureDocument: types.ACTION_DELETE_DOCUMENT,
    }),
    ...mapMutations({
      setErrorText: types.MUTATE_ERROR_TEXT,
      updateDocumentSharedWith: types.MUTATE_UPDATE_DOCUMENT_SHARED_WITH,
      setStepUpNotification: types.MUTATE_SET_NOTIFICATION_STEP_UP,
      setArchiveDocumentTrack: types.MUTATE_ARCHIVE_DOCUMENT_TRACK,
      setArchiveDocumentProgress: types.MUTATE_ARCHIVE_DOCUMENT_PROGRESS,
    }),
    hasNewNotification(documentId) {
      return this.notifications.some(
        notification =>
          notification.documentId === documentId &&
          notification.notificationEventType === notificationEventTypes.NEW_OR_UPDATED_SECURE_DOCUMENT
      );
    },
    getSingleExportEsdhSystemName(item) {
      if (this.getIsDisabledExportOption(item)) {
        return this.$t('document.esdh_system');
      }
      return this.getRecipientSystemNameByInstitutionCode(item.institutionCode);
    },
    getSingleExportEsdhLabel(item) {
      const systemName = this.getSingleExportEsdhSystemName(item);
      return this.$t('document.journalise_to_system', { system: systemName });
    },
    handleFilterChanged(value) {
      this.handlingFilter = value;
      this.refresh();
    },
    getCanShowActiveFilterIcon(value) {
      return this.handlingFilter === value;
    },
    getFilterLabel(value) {
      switch (value) {
        case DocumentFilterOptionEnum.UNREAD:
          return this.$t('label.unread');
        case DocumentFilterOptionEnum.LOCKED:
          return this.$t('label.filter_locked');
        case DocumentFilterOptionEnum.JOURNALIZED:
          return this.$t('document.journalized');
        case DocumentFilterOptionEnum.BEING_JOURNALIZED:
          return this.$t('document.journalization_in_progress');
        case DocumentFilterOptionEnum.JOURNALIZED_FAILED:
          return this.$t('document.journalization_failed');
        default:
          return this.$t('button.filter');
      }
    },
    handleSelectTemplate(templateId) {
      this.$router.push({ query: { skabelon: templateId } });
    },
    initDocuments() {
      if (this.isProfileLoaded) {
        if (this.hasPermission(this.permissionEnum.ACCESS_SECURE_FILESHARING)) {
          if (this.isSteppedUp) {
            this.getDocuments();
            this.getAccessibleInstitutionCodesForEsdhExport();
            this.getMaxDocumentsPerExport();
          } else {
            this.setStepUpNotification({
              showStepUpNotification: true,
              redirectedUrl: window.location.href,
              cancelUrl: '/dokumenter',
            });
          }
        } else {
          this.isLoading = false;
          this.setErrorText('NO_PERMISSION_TO_VIEW_SECURE_DOCUMENT');
          this.$router.push({ name: 'documents' });
        }
      }
    },
    getCanShowDeleteOption(document) {
      return this.hasPermissionOnInstitution(permissionEnum.SECURE_DOCUMENTS_ACCESS_ALL, document.institutionCode);
    },
    getCanDeleteDocument(document) {
      return document.journalingStatus === esdhJournalingStatus.IN_PROGRESS;
    },
    getCanExportToESDH(document) {
      return document.canExportToEsdh;
    },
    getCanRemoveOwnAssociation(document) {
      return document.canEdit;
    },
    getCanUnlock(document) {
      return (
        this.hasPermissionOnInstitution(permissionEnum.SECURE_DOCUMENTS_ACCESS_ALL, document.institutionCode) &&
        document.isLocked
      );
    },
    getCanEditLockStatus(document) {
      return !document.canEditLockedStatus;
    },
    getDisabledUnlockOptionTooltip(document) {
      if (document.journalingStatus === esdhJournalingStatus.IN_PROGRESS) {
        return this.$t('document.can_not_unlock_file_being_exported');
      }
      return '';
    },
    getIsDisabledExportOption(document) {
      return (
        !this.getHasEsdhConfiguration(document) ||
        document.journalingStatus === esdhJournalingStatus.IN_PROGRESS ||
        document.journalingStatus === esdhJournalingStatus.FAILED ||
        document.journalingStatus === esdhJournalingStatus.COMPLETED
      );
    },
    getEsdhExportOptionTooltipLabel(document) {
      if (!this.getHasEsdhConfiguration(document)) {
        return this.$t('document.no_esdh_system_specified');
      }

      return this.$t('document.can_not_journalize_already_journalized_document');
    },
    getHasEsdhConfiguration(document) {
      return this.institutionCodesCanExportToEsdh.indexOf(document.institutionCode) !== -1;
    },
    getJournalingStatusIcon(document) {
      if (!this.hasPermissionOnInstitution(permissionEnum.JOURNALING_TO_ESDH, document.institutionCode)) {
        return '';
      }

      switch (document.journalingStatus) {
        case esdhJournalingStatus.IN_PROGRESS:
          if (!this.getHasEsdhConfiguration(document)) {
            return '';
          }

          return iconClassEnum.SWITCH;
        case esdhJournalingStatus.COMPLETED:
          return iconClassEnum.DOCUMENT_BOX;
        case esdhJournalingStatus.FAILED:
          return iconClassEnum.WARNING;
        default:
          return '';
      }
    },
    getJournalingStatus(document) {
      switch (document.journalingStatus) {
        case esdhJournalingStatus.IN_PROGRESS:
          return this.$t('document.journalization_in_progress');
        case esdhJournalingStatus.COMPLETED:
          return this.getCompletedJournalingStatusTooltipLabel(document);
        case esdhJournalingStatus.FAILED:
          return this.$t('document.journalization_failed');
        default:
          return '';
      }
    },
    getCompletedJournalingStatusTooltipLabel(document) {
      return this.$t('document.journalization_completed', {
        systemName: document.esdhRecipientNames.join(', '),
      });
    },
    handleEsdhExportClicked(item) {
      this.selectedEsdhDocumentsToExport = [item];
      this.recipientSystemName = this.getSingleExportEsdhSystemName(item);

      if (!this.isValidToEsdhExport) {
        this.handleEsdhWarningModals();
        return;
      }

      this.$refs.esdhExportDrawer.show();
    },
    handleMultipleEsdhExportClicked() {
      this.selectedEsdhDocumentsToExport = this.documentItems.documents.filter(
        document => this.status.indexOf(document.id) !== -1
      );
      this.recipientSystemName = this.multipleExportsEsdhSystemName;

      if (!this.isValidToEsdhExport) {
        this.handleEsdhWarningModals();
        return;
      }

      this.$refs.esdhExportDrawer.show();
    },
    handleMultipleRemoveAssociationClicked() {
      this.selectedDocumentsToRemoveAssociation = this.documentItems.documents.filter(
        document => this.status.indexOf(document.id) !== -1
      );

      if (this.hasInvalidDocumentsToRemoveAssociation) {
        this.$refs.removeAssociationWithoutEditRightWarningModal.show();
        return;
      }

      this.$refs.associationRemovalDrawer.show();
    },
    handleRemoveAssociationClicked(item) {
      this.selectedDocumentsToRemoveAssociation = [item];

      if (this.hasInvalidDocumentsToRemoveAssociation) {
        this.$refs.removeAssociationWithoutEditRightWarningModal.show();
        return;
      }

      this.$refs.associationRemovalDrawer.show();
    },
    handleEsdhWarningModals() {
      if (
        this.hasDocumentsWithExceededChildren ||
        this.hasDocumentsWithNoChildren ||
        this.hasBeingJournalizedDocumentsToExport
      ) {
        this.$refs.esdhExportInvalidDocumentsWarningModal.show();
        return;
      }
      if (this.hasDocumentsWithoutJournalizationRight || this.hasDocumentsWithoutRecipientConfigured) {
        this.$refs.noExportRightWarningModal.show();
      }
    },
    onRemoveSelectedEsdhExportSecurefile(documentId) {
      const removeIndex = this.selectedEsdhDocumentsToExport.findIndex(document => document.id === documentId);
      this.selectedEsdhDocumentsToExport.splice(removeIndex, 1);

      if (this.selectedEsdhDocumentsToExport.length === 0) {
        this.$refs.esdhExportDrawer.hide();
      }
    },
    onRemoveAssociationRemovalDocument(documentId) {
      const removeIndex = this.selectedDocumentsToRemoveAssociation.findIndex(document => document.id === documentId);
      this.selectedDocumentsToRemoveAssociation.splice(removeIndex, 1);

      if (this.selectedDocumentsToRemoveAssociation.length === 0) {
        this.$refs.associationRemovalDrawer.hide();
      }
    },
    onSendingRemoveAssociation(documentIds) {
      this.handleDeleteNotifications(documentIds);
      this.refresh();
    },
    handleDeleteNotifications(documentIds) {
      const notifications = this.notifications.filter(
        notification =>
          documentIds.includes(notification.documentId) &&
          notification.notificationEventType === notificationEventTypes.NEW_OR_UPDATED_SECURE_DOCUMENT
      );

      if (notifications.length === 0) {
        return;
      }

      const payload = notifications.map(notification => ({
        notificationId: notification.notificationId,
        institutionProfileId: notification.institutionProfileId,
      }));

      this.deleteNotifications({
        notifications: payload,
      });
    },
    getDocumentDescription(doc) {
      switch (doc.documentType) {
        case documentTypes.NOTE:
          return truncate(doc.description ?? '', { length: MAX_SECURE_NOTE_DESCRIPTION_LENGTH });
        case documentTypes.RICHDOCUMENT:
          return doc.templateTitle ?? '';
        default:
          return '';
      }
    },
    getDeletedSecureDocumentParams(documentInfo) {
      return {
        index: documentInfo.index,
        limit: documentInfo.limit,
        sortings: documentInfo.sortings,
        filterRegardingGroupIds: documentInfo.filterRegardingGroupIds,
        filterDocumentCategories: documentInfo.filterDocumentCategories,
        filterRegardingStudentIds: documentInfo.filterRegardingStudentIds,
        selectedInstitutionCode: this.selectedInstitution.institutionCode,
      };
    },
    getSecureDocumentParams(documentInfo) {
      return {
        index: documentInfo.index,
        limit: documentInfo.limit,
        sortings: documentInfo.sortings,
        filterRegardingGroupIds: documentInfo.filterRegardingGroupIds,
        filterDocumentCategories: documentInfo.filterDocumentCategories,
        filterRegardingStudentIds: documentInfo.filterRegardingStudentIds,
        filterInstitutionProfileIds: documentInfo.filterInstitutionProfileIds,
        filterUnread: this.handlingFilter === DocumentFilterOptionEnum.UNREAD,
        filterLocked: this.handlingFilter === DocumentFilterOptionEnum.LOCKED,
        filterJournalingStatus: this.getFilterJournalingStatusPayloadValue(),
      };
    },
    getFilterJournalingStatusPayloadValue() {
      switch (this.handlingFilter) {
        case DocumentFilterOptionEnum.JOURNALIZED:
          return esdhJournalingStatus.COMPLETED;
        case DocumentFilterOptionEnum.BEING_JOURNALIZED:
          return esdhJournalingStatus.IN_PROGRESS;
        case DocumentFilterOptionEnum.JOURNALIZED_FAILED:
          return esdhJournalingStatus.FAILED;
        default:
          return null;
      }
    },
    getDocumentsTableDate(item) {
      return this.includesDeleted
        ? `${DateTimeUtil.shortDate(item.deletedAt, true)}`
        : `${DateTimeUtil.shortDate(item.updatedAt, true)}`;
    },
    getDocumentsTableTime(item) {
      return this.includesDeleted
        ? `kl. ${DateTimeUtil.formatDate(item.deletedAt, 'LT')}`
        : `kl. ${DateTimeUtil.formatDate(item.updatedAt, 'LT')}`;
    },
    loadSecureDocuments(payload) {
      if (this.includesDeleted) {
        return this.loadDeletedDocuments(payload);
      }
      return this.loadDocuments(payload);
    },
    loadMoreSecureDocuments(payload) {
      if (this.includesDeleted) {
        return this.appendDeletedDocuments(payload);
      }
      return this.appendDocuments(payload);
    },
    onRestoreClicked(secureDocument) {
      this.selectedDocument = secureDocument;
      this.$refs.restoreWarningModal.show();
    },
    confirmRestoringDeletedSecureDocument() {
      this.restoreDeletedSecureDocument({ documentId: this.selectedDocument.id }).then(() => {
        this.refresh();
        this.$refs.restoreWarningModal.hide();
      });
    },
    cancelRestoring() {
      this.$refs.restoreWarningModal.hide();
    },
    confirmRemoveInvalidDocuments() {
      if (!this.hasValidEsdhDocuments && this.selectedEsdhDocumentsToExport.length === 1) {
        this.cancelRemoveInvalidDocuments();
        return;
      }
      if (this.hasDocumentsWithExceededChildren) {
        this.confirmRemoveExceedRegardingChildrenDocuments();
        return;
      }
      if (this.hasDocumentsWithNoChildren) {
        this.confirmRemoveNoRegardingChildrenDocuments();
        return;
      }
      if (this.hasBeingJournalizedDocumentsToExport) {
        this.confirmRemoveBeingJournalizedDocuments();
      }
    },
    confirmRemoveExceedRegardingChildrenDocuments() {
      this.selectedEsdhDocumentsToExport = this.selectedEsdhDocumentsToExport.filter(
        document => !this.isDocumentExceedRegardingChildren(document)
      );
      this.handleConfirmInvalidDocumentsWarningModal();
    },
    confirmRemoveNoRegardingChildrenDocuments() {
      this.selectedEsdhDocumentsToExport = this.selectedEsdhDocumentsToExport.filter(
        document => !this.isDocumentHasNoRegardingChildren(document)
      );
      this.handleConfirmInvalidDocumentsWarningModal();
    },
    confirmRemoveBeingJournalizedDocuments() {
      this.selectedEsdhDocumentsToExport = this.selectedEsdhDocumentsToExport.filter(
        document => !this.isDocumentBeingJournalized(document)
      );
      this.handleConfirmInvalidDocumentsWarningModal();
    },
    handleConfirmInvalidDocumentsWarningModal() {
      if (this.selectedEsdhDocumentsToExport.length === 0) {
        this.$refs.esdhExportInvalidDocumentsWarningModal.hide();
        return;
      }

      this.$refs.esdhExportInvalidDocumentsWarningModal.hide();

      if (!this.isValidToEsdhExport) {
        this.handleEsdhWarningModals();
        return;
      }

      this.$refs.esdhExportDrawer.show();
    },
    cancelRemoveInvalidDocuments() {
      this.$refs.esdhExportInvalidDocumentsWarningModal.hide();
    },
    confirmRemoveDocumentsWithoutExportRight() {
      if (!this.hasValidEsdhDocuments && this.selectedEsdhDocumentsToExport.length === 1) {
        this.cancelRemoveDocumentsWithoutExportRight();
        return;
      }
      if (this.hasDocumentsWithoutJournalizationRight) {
        this.confirmRemoveDocumentsWithoutJournalizationRight();
        return;
      }
      if (this.hasDocumentsWithoutRecipientConfigured) {
        this.confirmRemoveDocumentsWithoutRecipientConfigured();
      }
    },
    cancelRemoveDocumentsWithoutExportRight() {
      this.$refs.noExportRightWarningModal.hide();
    },
    confirmRemoveDocumentsWithoutJournalizationRight() {
      this.selectedEsdhDocumentsToExport = this.selectedEsdhDocumentsToExport.filter(
        document => !this.isDocumentWithoutJournalizationRight(document)
      );
      this.handleConfirmNoExportRightWarningModal();
    },
    confirmRemoveDocumentsWithoutRecipientConfigured() {
      this.selectedEsdhDocumentsToExport = this.selectedEsdhDocumentsToExport.filter(
        document =>
          !this.isDocumentWithoutJournalizationRight(document) && !this.isDocumentWithoutRecipientConfigured(document)
      );
      this.handleConfirmNoExportRightWarningModal();
    },
    handleConfirmNoExportRightWarningModal() {
      if (this.selectedEsdhDocumentsToExport.length === 0) {
        this.$refs.noExportRightWarningModal.hide();
        return;
      }

      this.$refs.noExportRightWarningModal.hide();

      if (!this.isValidToEsdhExport) {
        this.handleEsdhWarningModals();
        return;
      }

      this.$refs.esdhExportDrawer.show();
    },
    confirmRemoveDocumentAssociationWithoutEditRight() {
      this.selectedDocumentsToRemoveAssociation = this.selectedDocumentsToRemoveAssociation.filter(
        document => document.canEdit
      );
      this.$refs.removeAssociationWithoutEditRightWarningModal.hide();

      if (this.selectedDocumentsToRemoveAssociation.length > 0) {
        this.$refs.associationRemovalDrawer.show();
      }
    },
    navigateToSecureList() {
      this.$router.push({ name: 'documentsSecureList' });
    },
    navigateToEditForm({ documentType, id }) {
      this.$router.push({
        name: 'documentsSecureEdit',
        params: { documentType, documentId: id },
      });
    },
    onBottomReached() {
      if (this.isLoading || !this.documentItems.moreDocumentsExists) {
        return;
      }
      this.getMoreDocuments();
    },
    getSearchScope() {
      if (this.profile.role === this.portalRoles.EMPLOYEE) {
        return [this.portalRoles.EMPLOYEE, this.portalRoles.GUARDIAN];
      } else {
        return [this.portalRoles.EMPLOYEE];
      }
    },
    toggleLockDocument(document, isLocked = true) {
      this.changedDocument = document;
      this.isLocked = isLocked;

      if (isLocked) {
        this.lockDocumentWarning = this.$getText('DOCUMENTS_ALERT_LOCK_DOCUMENT_1', {
          documentName: document.title,
        });
        this.$refs.warningLockDocument.show();
        return;
      } else if (document.journalingStatus === esdhJournalingStatus.IN_PROGRESS) {
        this.$refs.warningUnlockBeingExportedDocument.show();
        return;
      }

      this.doLockDocument();
    },
    resetLockDocument() {
      this.changedDocument = null;
      this.isLocked = null;
      this.lockDocumentWarning = null;
      this.$refs.warningLockDocument.hide();
    },
    doLockDocument() {
      this.lockDocument({
        documentId: this.changedDocument.id,
        isLocked: this.isLocked,
      }).then(() => {
        if (
          this.changedDocument.documentType === documentTypes.RICHDOCUMENT ||
          this.changedDocument.documentType === documentTypes.NOTE
        ) {
          this.editSetupDocument({
            id: this.changedDocument.id,
            isUpdateInList: true,
          });
        } else if (this.changedDocument.documentType === documentTypes.EXTERNAL) {
          this.editSetupExternal({
            id: this.changedDocument.id,
            isUpdateInList: true,
          });
        }
        this.resetLockDocument();
      });
    },
    copyLink(id, type) {
      const url = 'https://' + location.host + '/#/dokumenter/sikre/' + type + '/' + id;
      this.copyStringToClipboard(url);
    },
    copyStringToClipboard(str) {
      const el = document.createElement('textarea');
      el.value = str;
      el.setAttribute('readonly', '');
      el.style = { position: 'absolute', left: '-9999px' };
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.$refs.copyLinkToastr.show();
    },
    showModalExportPDF(item) {
      if (this.status.length > this.maxDocumentsPerExport) {
        this.$refs.exceededDocumentsExportToPdfWarningModal.show();
        return;
      }

      this.selectedDocWithoutExportRight = this.documentItems.documents.filter(
        item =>
          this.status.indexOf(item.id) !== -1 &&
          !this.hasPermissionOnInstitution(permissionEnum.EXPORT_SECURE_FILES, item.institutionCode)
      );
      if (this.selectedDocWithoutExportRight.length > 0) {
        this.$refs.modalSelectedWithoutExportWarning.show();
        return;
      }

      this.fileProgress = 0;
      this.isDisabledExport = true;
      this.isExportFailed = false;
      this.setArchiveDocumentTrack(null);
      this.setArchiveDocumentProgress(null);
      this.$refs.modalExportPDF.show();
      const creatingPayload = {
        secureDocumentIds: item ? [item.id] : this.status,
      };

      this.createArchiveSecureDocuments(creatingPayload).then(() => {
        this.archiveDocumentTrackingTimer = setInterval(() => {
          if (!this.$refs.modalExportPDF.showAulaModal) {
            clearInterval(this.archiveDocumentTrackingTimer);
            this.setArchiveDocumentTrack(null);
            this.setArchiveDocumentProgress(null);
          } else {
            const trackingPayload = {
              requestId: this.archiveDocumentTrack,
            };

            this.trackArchiveSecureDocuments(trackingPayload)
              .then(() => {
                if (this.archiveDocumentProgress) {
                  this.fileProgress = this.archiveDocumentProgress.progress * 100;
                  if (this.archiveDocumentProgress.status === exportingStatus.COMPLETED) {
                    clearInterval(this.archiveDocumentTrackingTimer);
                    this.isDisabledExport = false;
                  }
                }
              })
              .catch(() => {
                clearInterval(this.archiveDocumentTrackingTimer);
                this.isExportFailed = true;
              });
          }
        }, 3000);
      });
    },
    openFileUrl() {
      if (this.archiveDocumentProgress && this.archiveDocumentProgress.status === exportingStatus.COMPLETED) {
        window.open(this.archiveDocumentProgress.fileUrl);
      }
    },
    onToggleClicked(checked) {
      this.allSelected = checked.length === this.documentItems.documents.length;
    },
    onToggleAllClicked(checked) {
      if (!checked) {
        this.status = [];
        return;
      }

      this.markAllDocumentsAsSelected();
    },
    markAllDocumentsAsSelected() {
      this.status = this.documentItems.documents.map(doc => doc.id);
    },
    resetSelectAll() {
      this.allSelected = false;
    },
    handleClearSelectedFilterPersonFilter() {
      this.selectedFilterPerson = null;
      this.updateFilter();
    },
    handleClearSelectedCategoryFilter() {
      this.selectedFilterCategory = null;
      this.updateFilter();
    },
    updateFilter(group) {
      this.resetSelectAll();
      this.paginationStart = 0;
      this.$nextTick(() => {
        if (group) {
          this.selectedFilterGroup = group.map(g => g.id);
        }
        this.getDocuments();
      });
    },
    setSortOrder(sortItem) {
      this.resetSelectAll();
      this.paginationStart = 0;
      if (this.sort == sortItem) {
        this.sortDirection = this.sortDirection == 'asc' ? 'desc' : 'asc';
      } else {
        this.sort = sortItem;
        if (sortItem === sortTypes.UPDATED_AT) {
          this.sortDirection = 'desc';
        } else {
          this.sortDirection = 'asc';
        }
      }
      this.getDocuments();
    },
    refresh() {
      this.resetSelectAll();
      this.paginationStart = 0;
      this.getDocuments();
    },
    resetFilters() {
      this.handlingFilter = null;
    },
    getDocuments() {
      if (this.documentsCancelTokenSource !== null) {
        this.cancelAxiosRequest(this.documentsCancelTokenSource);
        this.resetDocumentsCancelToken();
      }
      if (this.paginationStart == 0) {
        this.status = [];
      }
      this.createDocumentsCancelToken();

      let promise;
      this.isLoading = true;

      if (this.paginationStart === 0) {
        promise = this.loadSecureDocuments(this.documentPayload);
      } else {
        promise = this.loadMoreSecureDocuments(this.documentPayload);
      }

      promise.then(() => {
        this.populateFilters();
        this.isLoading = false;
      });
    },
    populateFilters() {
      const toOption = (value, text) => ({ value, text });
      const allChildrenOption = toOption(null, this.$t('label.search_by_child'));
      const allCategoriesOption = toOption(null, this.$t('label.search_by_category'));

      const filters = this.documentItems.filters ?? [];
      const children = filters.regardingInstitutionProfiles ?? [];
      const categories = filters.documentCategories ?? [];

      const sortedChildren = sortBy(children, ['name']);

      this.filterPerson = [
        allChildrenOption,
        ...sortedChildren.map(item => toOption(item.id, this.$getProfileDisplayName(item))),
      ];

      this.filterCategory = [allCategoriesOption, ...categories.map(item => toOption(item, item))];
    },
    getMoreDocuments() {
      this.paginationStart++;
      this.getDocuments();
    },
    editElement(documentId, documentType) {
      this.handleDeleteNotifications([documentId]);
      this.$router.push({
        name: 'documentsSecureEdit',
        params: { documentId, documentType },
      });
    },
    downloadSensitiveFile() {
      this.$refs.warningDownloadSensitiveFile.hide();
      this.editSetupExternal({ id: this.downloadFile.documentId }).then(response => {
        if (!response.code) {
          let documentUrl = null;
          if (this.liveDocument.attachment.file != null) {
            documentUrl = this.liveDocument.attachment.file.url;
          } else if (this.liveDocument.attachment.media != null) {
            documentUrl = this.liveDocument.attachment.media.file.url;
          } else if (this.liveDocument.attachment.link != null) {
            documentUrl = this.liveDocument.attachment.link.url;
          }
          if (documentUrl != null) {
            window.open(documentUrl, '_blank');
          }
        } else {
          this.setErrorText('NO_PERMISSION_TO_VIEW_SECURE_SINGLE_DOCUMENT');
        }
      });
      this.downloadFile = null;
    },
    rowClickAction(file) {
      this.handleDeleteNotifications([file.id]);
      this.$router.push({
        name: 'viewDocument',
        params: {
          documentType: file.documentType,
          documentId: file.id,
          includesDeleted: this.includesDeleted,
        },
      });
    },
    downloadDocument(file) {
      if (file.documentType == documentTypes.EXTERNAL) {
        this.downloadFile = {
          documentType: file.documentType,
          documentId: file.id,
        };
        this.$refs.warningDownloadSensitiveFile.show();
      } else {
        this.handleDeleteNotifications([file.id]);
        this.$router.push({
          name: 'viewDocument',
          params: { documentType: file.documentType, documentId: file.id },
        });
      }
    },
    seeHistory(document) {
      this.$router.push({
        path: '/dokumenter/' + document.documentType + '/' + document.id + '/historie',
      });
    },
    sharedUsersAndGroupsStore(multi) {
      let documentIds = [];
      let resetSharings = true;
      this.intersectedChildrenIds = [];
      if (multi) {
        documentIds = this.status;
        resetSharings = false;
      } else {
        documentIds = [this.singleStatus];
      }
      const sharedInstitutionProfiles = [];
      const sharedGroups = [];
      this.currentShared.forEach(function (entry) {
        if (entry.profileId) {
          sharedInstitutionProfiles.push({
            institutionProfileId: entry.profileId,
            canEdit: entry.canEdit || false,
          });
        } else {
          sharedGroups.push({
            groupId: entry.groupId,
            institutionCode: entry.institutionCode,
            canEdit: entry.canEdit || false,
          });
        }
      });
      this.isUpdateSharing = true;
      const documentIdsChunks = chunk(documentIds, UPDATE_SHARING_CHUNK_SIZE);
      const editSharingPromises = documentIdsChunks.map(documentIds =>
        this.editSharings({
          documentIds,
          sharedInstitutionProfiles,
          sharedGroups,
          resetSharings,
        })
      );

      Promise.all(editSharingPromises)
        .then(() => {
          this.$refs.modalSharedWith.hide();
          this.$refs.modalSharedWithGroups.hide();
          this.isUpdateSharing = false;
          this.status = [];
          this.currentShared = [];
          this.singleStatus = null;
          this.refresh();
        })
        .catch(error => {
          if (error?.response?.data?.status?.subCode == errorSubCodeTypes.errorShareWithOtherMunicipalities) {
            this.isUpdateSharing = false;
            this.$refs.shareWithOtherMunicipalitiesError.show();
          }
        });
    },
    showModalSharedWith(objItem, docType) {
      const id = objItem;
      this.singleStatus = id;
      const params = {
        id,
      };

      if (docType == documentTypes.EXTERNAL) {
        this.editSetupExternal(params).then(() => {
          this.currentShared = [];
          this.workingDocument = this.liveDocument;
          this.appendSharedUsersAndGroupsSet = [];
          this.workingDocument.regardingInstitutionProfileIds = this.workingDocument.associatedInstitutionProfiles.map(
            child => child.id
          );
          for (const item of this.workingDocument.sharedWithGroups) {
            this.currentShared.push({
              groupId: item.id,
              displayName: item.name,
              canEdit: item.canEdit,
              role: item.role,
            });
          }
          for (const item of this.workingDocument.sharedWithInstitutionProfiles) {
            this.currentShared.push({
              profileId: item.id,
              displayName: this.$getProfileDisplayName(item),
              canEdit: item.canEdit,
              role: item.role,
            });
          }
          this.$refs.modalSharedWith.show();
        });
      } else {
        this.editSetupDocument(params).then(() => {
          this.currentShared = [];
          this.workingDocument = this.liveDocument;
          this.appendSharedUsersAndGroupsSet = [];
          this.workingDocument.regardingInstitutionProfileIds = this.workingDocument.associatedInstitutionProfiles.map(
            child => child.id
          );
          for (const item of this.workingDocument.sharedWithGroups) {
            this.currentShared.push({
              groupId: item.id,
              displayName: item.name,
              canEdit: item.canEdit,
              role: item.role,
            });
          }
          for (const item of this.workingDocument.sharedWithInstitutionProfiles) {
            this.currentShared.push({
              profileId: item.id,
              displayName: this.$getProfileDisplayName(item),
              canEdit: item.canEdit,
              role: item.role,
            });
          }
          this.$refs.modalSharedWith.show();
        });
      }
    },
    appendSharedUsersAndGroups(users) {
      if (users.length > 0) {
        for (const user of users) {
          if (user.type == docTypes.PROFILE.toLowerCase()) {
            if (this.currentShared.filter(e => e.profileId == user.id).length == 0) {
              this.currentShared.push({
                profileId: user.id,
                canEdit: this.getCanEditMultipleDocuments(user),
                displayName: user.label,
                shortName: null,
                role: user.portalRole,
              });
            }
            if (
              !user.fromGroup &&
              !this.institutions.find(institution => institution.institutionCode == user.institutionCode) &&
              !this.institutions.find(institution => institution.municipalityCode == user.municipalityCode)
            ) {
              this.$refs.warningShareWithDifferentInstitution.show();
            }
          }
        }
      }
      this.appendSharedUsersAndGroupsSet = [];
      this.resetSharedWithInput = !this.resetSharedWithInput;
    },
    getCanEditMultipleDocuments(user) {
      return this.getIsCreatorOfSelectedDocuments(user) || this.getHasEditRightToSelectedDocuments(user);
    },
    getIsCreatorOfSelectedDocuments(user) {
      return (
        this.selectedDocuments.length > 0 &&
        this.selectedDocuments.every(item => item.creator.id === user.id && item.canEdit)
      );
    },
    getHasEditRightToSelectedDocuments(user) {
      return (
        this.selectedDocuments.length > 0 &&
        this.selectedDocuments.every(item =>
          item.sharedWithInstitutionProfiles.find(profile => profile.id === user.id && profile.canEdit)
        )
      );
    },
    addSharingGroups() {
      this.selectedLockedDocuments = this.documentItems.documents.filter(
        item => this.status.indexOf(item.id) !== -1 && item.canEdit === false
      );
      this.$nextTick(() => {
        if (this.status.length && !this.selectedLockedDocuments.length) {
          this.currentShared = [];
          this.findIntersectedChildrenIds();
          this.$refs.modalSharedWithGroups.show();
        } else {
          this.$refs.modalSelectedWarning.show();
        }
      });
    },
    findIntersectedChildrenIds() {
      let result = [];

      if (this.documentItems && this.documentItems.documents) {
        const selectedDocuments = this.documentItems.documents.filter(doc => this.status.includes(doc.id));

        if (selectedDocuments && selectedDocuments.length > 0) {
          const childrenIdArrays = selectedDocuments.map(doc =>
            doc.associatedInstitutionProfiles.map(child => child.id)
          );
          result = childrenIdArrays.reduce((arr1, arr2) => arr1.filter(item => arr2.includes(item)));
        }
      }
      this.intersectedChildrenIds = result;
    },
    createDocumentsCancelToken() {
      this.documentsCancelTokenSource = this.getAxiosCancelTokenSource();
    },
    resetDocumentsCancelToken() {
      this.documentsCancelTokenSource = null;
    },
    deleteDocument() {
      this.deleteSecureDocument({ id: this.selectedDocument.id }).then(() => {
        this.refresh();
        this.$refs.deleteSecureDocumentConfirmModal.hide();
      });
    },
    openDeleteDocumentConfirmModal(item) {
      this.$refs.deleteSecureDocumentConfirmModal.show();
      this.selectedDocument = item;
    },
    async getMaxDocumentsPerExport() {
      try {
        this.maxDocumentsPerExport = await documentService.getMaxDocumentsPerExport();
      } catch {
        this.setErrorText('API_ERROR_GENERAL');
      }
    },
    async getAccessibleInstitutionCodesForEsdhExport() {
      this.institutionCodesCanExportToEsdh = [];
      this.esdhSystemNameByMunicipalityCode.clear();
      try {
        for (const activeInstitution of this.currentActiveInstitutions) {
          const institutionCode = activeInstitution.institutionCode;

          if (!this.hasPermissionOnInstitution(permissionEnum.JOURNALING_TO_ESDH, institutionCode)) {
            continue;
          }

          const recipientNames = await esdhDataExportAdminService.getRecipientNames(institutionCode);

          if (recipientNames.length > 0) {
            this.institutionCodesCanExportToEsdh.push(institutionCode);
            this.populateEsdhRecipientSystemName(activeInstitution, recipientNames[0]);
          }
        }
      } catch {
        this.setErrorText('API_ERROR_GENERAL');
      }
    },
    populateEsdhRecipientSystemName(institution, recipientName) {
      if (!this.esdhSystemNameByMunicipalityCode.get(institution.municipalityCode)) {
        this.esdhSystemNameByMunicipalityCode.set(institution.municipalityCode, recipientName);
      }
    },
    getRecipientSystemNameByInstitutionCode(instCode) {
      const institution = this.currentActiveInstitutions.find(institution => institution.institutionCode === instCode);
      return this.esdhSystemNameByMunicipalityCode.get(institution.municipalityCode);
    },
    isDocumentExceedRegardingChildren(document) {
      return document.associatedInstitutionProfiles.length > CHILD_LIMIT_FOR_ESDH_EXPORT;
    },
    isDocumentHasNoRegardingChildren(document) {
      return document.associatedInstitutionProfiles.length === 0;
    },
    isDocumentBeingJournalized(document) {
      return document.journalingStatus === esdhJournalingStatus.IN_PROGRESS;
    },
    isDocumentWithoutJournalizationRight(document) {
      return !this.hasPermissionOnInstitution(permissionEnum.JOURNALING_TO_ESDH, document.institutionCode);
    },
    isDocumentWithoutRecipientConfigured(document) {
      return this.institutionCodesCanExportToEsdh.indexOf(document.institutionCode) === -1;
    },
    isDocumentValidForEsdhExport(document) {
      return (
        !this.isDocumentExceedRegardingChildren(document) &&
        !this.isDocumentHasNoRegardingChildren(document) &&
        !this.isDocumentBeingJournalized(document) &&
        !this.isDocumentWithoutJournalizationRight(document) &&
        !this.isDocumentWithoutRecipientConfigured(document)
      );
    },
  },
};
</script>

<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/documents/_overview' as *;

.restore-icon {
  --font-size: 1.8rem;
}

.restore-btn {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-left: 0;
  width: 100px;

  .restore-text {
    margin-top: 4px;
    font-weight: 400;
    text-transform: capitalize;
  }
}

.deleted-secure-documents-text-container {
  margin-bottom: 20px;

  .header-text {
    font-size: 32px;
    font-weight: 800;
    line-height: 40px;
    margin-bottom: 10px;
  }

  .description-text {
    font-size: 15px;
    font-weight: 400;
  }
}

.edit-document {
  width: auto;
  height: 30px;
}

.document-icon-container {
  width: 100%;
  height: 30px;
  background-color: transparent;

  .aula-icon {
    font-size: 20px;
  }

  &[data-warning] {
    color: var(--color-alert);
  }
}

.document-export-status {
  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--color-primary-darker);
  font-size: 14px;
  font-weight: 400;
  min-width: 120px;
}

.table-cell.check {
  width: 40px;
  padding: 0 12px 0 2px !important;

  .custom-checkbox {
    padding: 4px;
    margin-bottom: 0;
    left: 30px;
    top: -3px;
  }
}

.table-cell.actions {
  .custom-checkbox {
    margin-bottom: 20px;
    left: -12px;
  }
}

.upload-link {
  cursor: pointer;
  float: left;
  margin-top: 12px;
}

.submit-row {
  margin-top: 15px;
}

.shared-with-modal {
  .row {
    border-bottom: solid 1px #eee;

    .col,
    .col-1,
    .col-3 {
      padding: 10px;
    }
  }

  .container-scroll ::v-deep .aula-search .el-input__inner {
    background-color: var(--color-grey-light);
  }
}

.badge-notification.documents {
  --position: unset;
  margin-inline-end: 10px;
  float: left;

  ::v-deep .aula-badge {
    width: 18px;
    height: 18px;

    i:before {
      content: '';
    }
  }
}

.table-cell.collapsed-only ::v-deep .badge {
  position: relative;
  top: 9px;
  left: 30px;
}

.drawer-content .aula-spinner {
  width: 80px;
  height: 80px;
  position: absolute;
  right: 40px;
  top: -12px;
}

.show-more {
  padding: 20px 0;
  text-align: center;

  button {
    float: none;
  }
}

.table-controls {
  position: relative;
  display: flex;
}

.sort {
  margin-top: auto;
}

.tooltip {
  div {
    text-align: left !important;
  }
}

.choose-document {
  @include breakpoint-lg-down() {
    left: -10px !important;
  }
}

.filetype {
  color: var(--color-primary-dark-guardian);

  .theme-employee & {
    color: var(--color-primary-dark-employee);
  }
}

.mobile-left {
  @include breakpoint-lg-down() {
    width: 100%;
  }
}

::v-deep .table {
  overflow-wrap: anywhere;

  .table-row {
    &.header {
      &[data-sticky] {
        position: sticky;
        top: 176px;
        z-index: 2;

        &[data-padding-top] {
          top: 236px;
        }
      }

      &[data-parent-type='administration'] {
        font-size: 15px;
        font-weight: 700;
      }
    }

    &.mobile-header {
      display: table-row;
      position: sticky;
      top: 106px;
      z-index: 2;
      background-color: var(--color-grey-dark);

      .table-cell {
        display: table-cell;
        padding: 15px;
        border-right: solid 2px transparent;
        border-top: solid 2px var(--color-grey-light);
        border-bottom: solid 2px var(--color-grey-light);

        .custom-checkbox {
          left: 4px;
        }
      }

      @include breakpoint-lg() {
        top: 94px;
      }

      @include breakpoint-xl() {
        display: none;
      }
    }
  }

  .table-row:focus {
    position: relative;
  }

  .table-cell {
    .dropdown-select {
      .dropdown-menu {
        max-width: 350px !important;

        .disabled-drop-down .dropdown-item.disabled {
          background-color: transparent;
        }
      }
    }
  }
}

.no-documents {
  width: 100%;
  margin: 20px 0;
  font-weight: bold;
}

.documents-secure-list-container {
  @include breakpoint-lg-down() {
    padding-bottom: var(--menu-item-height);
  }
  @include breakpoint-sm-down {
    .aula-search-outer {
      margin-bottom: 4px;
    }
  }
}

.marking-text,
.alert-text {
  color: var(--color-alert);
}

.multiple-file-actions {
  clear: both;
  margin-bottom: 10px;
  overflow: auto;
  width: 100%;
  display: flex;
  gap: 16px;
  justify-content: flex-end;

  .btn {
    text-transform: uppercase;
    width: auto;
  }

  @include breakpoint-xl-down() {
    .btn {
      margin-right: 10px;
      margin-bottom: 8px;
    }
  }
}

.sort {
  &.asc::before,
  &.desc::before {
    margin-left: 3px !important;
    margin-right: -3px !important;
    color: unset !important;
  }
}

.header-actions-container {
  &[data-sticky] {
    @include breakpoint-xl() {
      position: sticky;
      top: 58px;
      z-index: 3;
      background-color: var(--color-grey-light);
      padding-top: 8px;
      padding-bottom: 2px;
    }
  }
}

.secure-files-filter-container {
  display: flex;
  align-items: center;
  background-color: var(--color-grey-light);

  &[data-sticky] {
    position: sticky;
    z-index: 3;
    top: 60px;
    min-height: 48px;

    @include breakpoint-lg() {
      min-height: 36px;
      top: 60px;
    }

    @include breakpoint-xl() {
      top: 142px;

      &[data-padding-top] {
        top: 202px;
      }
    }
  }

  .dropdown-filter-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;

    .filter-dropdown {
      --dropdown-max-width: 240px;

      .filter-arrow {
        --font-size: 0.4rem;
        margin-left: 5px;
      }

      .dropdown-item {
        .active-filter-icon {
          position: absolute;
          left: 9px;
          top: 16px;
          --font-size: 10px;
          color: var(--color-primary-base);
          opacity: 0.8;
        }

        &:hover {
          .active-filter-icon {
            opacity: 1;
          }
        }
      }
    }
  }

  .selected-document-count {
    height: 100%;
    display: flex;
    align-items: center;
    padding-left: 2px;
  }
}
</style>
